import React from 'react';
import { Building2, TrendingUp, Users, Target, ExternalLink } from 'lucide-react';
import type { ProjectSimilarity } from '../../types/essReference';

interface SimilarProjectsPanelProps {
  similarProjects: ProjectSimilarity[];
  onViewProject?: (projectId: string) => void;
}

export const SimilarProjectsPanel: React.FC<SimilarProjectsPanelProps> = ({
  similarProjects,
  onViewProject
}) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(value);
  };

  if (similarProjects.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Building2 className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Projets Similaires</h3>
        </div>
        <p className="text-gray-600 text-sm">
          Aucun projet similaire trouvé. Définissez vos objectifs pour voir des suggestions.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-center space-x-2 mb-6">
        <Building2 className="w-5 h-5 text-blue-600" />
        <h3 className="text-lg font-semibold text-gray-900">Projets Similaires</h3>
        <span className="ml-auto text-sm text-gray-500">
          {similarProjects.length} projet{similarProjects.length > 1 ? 's' : ''} trouvé{similarProjects.length > 1 ? 's' : ''}
        </span>
      </div>

      <div className="space-y-4">
        {similarProjects.map((similarity, index) => {
          const project = similarity.project;
          const firstYearKey = Object.keys(project.data.finance.pnl)[0];
          const firstYearPNL = project.data.finance.pnl[firstYearKey];
          const firstYearImpact = project.data.impact.years[firstYearKey];

          return (
            <div
              key={project.id}
              className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer"
              onClick={() => onViewProject?.(project.id)}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <h4 className="font-semibold text-gray-900">{project.project_name}</h4>
                    {index === 0 && (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                        Meilleure correspondance
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{project.category}</p>
                  <p className="text-xs text-gray-500 mt-1">{project.territory}</p>
                </div>
                <div className="text-right">
                  <div className="flex items-center space-x-1 text-blue-600 mb-1">
                    <Target className="w-4 h-4" />
                    <span className="text-sm font-medium">{similarity.similarity_score}% match</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-3">
                {similarity.matching_factors.map((factor, idx) => (
                  <span
                    key={idx}
                    className="px-2 py-1 bg-green-50 text-green-700 text-xs rounded-full"
                  >
                    {factor}
                  </span>
                ))}
              </div>

              <div className="grid grid-cols-3 gap-4 pt-3 border-t border-gray-200">
                <div>
                  <div className="flex items-center space-x-1 text-gray-600 mb-1">
                    <TrendingUp className="w-3 h-3" />
                    <span className="text-xs">CA Année 1</span>
                  </div>
                  <p className="text-sm font-semibold text-gray-900">
                    {formatCurrency(firstYearPNL?.revenue_total_eur || 0)}
                  </p>
                </div>

                <div>
                  <div className="flex items-center space-x-1 text-gray-600 mb-1">
                    <TrendingUp className="w-3 h-3" />
                    <span className="text-xs">Marge EBITDA</span>
                  </div>
                  <p className="text-sm font-semibold text-gray-900">
                    {firstYearPNL?.ebitda_margin_pct?.toFixed(1) || 0}%
                  </p>
                </div>

                <div>
                  <div className="flex items-center space-x-1 text-gray-600 mb-1">
                    <Users className="w-3 h-3" />
                    <span className="text-xs">Emplois créés</span>
                  </div>
                  <p className="text-sm font-semibold text-gray-900">
                    {firstYearImpact?.jobs_created || 0}
                  </p>
                </div>
              </div>

              <div className="mt-3 flex items-center space-x-2 text-xs text-blue-600 hover:text-blue-700">
                <span>Voir les détails</span>
                <ExternalLink className="w-3 h-3" />
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 p-3 bg-green-50 rounded-lg">
        <p className="text-sm text-green-800">
          ✨ Ces projets ont des caractéristiques similaires aux vôtres. Explorez-les pour affiner votre stratégie.
        </p>
      </div>
    </div>
  );
};
